import { useEffect, useState } from 'react';
import { useAuth0 } from '@auth0/auth0-react';
import axios from 'axios';

const API_URL = process.env.REACT_APP_API_URL
  || 'http://localhost:5062';

export interface UserProfile {
  id: number;
  auth0Id: string;
  email: string;
  name: string;
  picture: string;
  plan: string;
  planId: number;
  generationsUsed: number;
  generationsLimit: number;
  generationsLeft: number;
}

export const useUser = () => {
  const {
    isAuthenticated,
    getAccessTokenSilently,
    getIdTokenClaims, 
    user: auth0User
  } = useAuth0();

  const [userProfile, setUserProfile] =
    useState<UserProfile | null>(null);
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    if (!isAuthenticated) {
      setUserProfile(null);
      return;
    }

    const syncUser = async () => {
      setLoading(true);
      try {
        const token = await getAccessTokenSilently({
          authorizationParams: {
            audience: process.env.REACT_APP_AUTH0_AUDIENCE
          }
        });

        const idClaims = await getIdTokenClaims();
        
        // Sync user with our database
        const response = await axios.post(
          `${API_URL}/api/auth/sync`,
          {
            email: idClaims?.email,
            name: idClaims?.name,
            picture: idClaims?.picture
          },
          {
            headers: {
              Authorization: `Bearer ${token}`
            }
          }
        );

        // Get full profile with generation count
        const profileResponse = await axios.get(
          `${API_URL}/api/auth/me`,
          {
            headers: {
              Authorization: `Bearer ${token}`
            }
          }
        );

        setUserProfile(profileResponse.data);
      } catch (err) {
        console.error('Failed to sync user:', err);
      } finally {
        setLoading(false);
      }
    };

    syncUser();
  }, [isAuthenticated, getAccessTokenSilently]);

  return { userProfile, loading };
};